// Main App component for Share Lattice
import { Routes, Route, Navigate } from 'react-router-dom'
import { lazy, Suspense } from 'react'
import { useAuthContext } from './contexts/auth-context'
import { Toaster } from 'sonner'
import AuthLayout from './layouts/auth-layout'
import DashboardLayout from './layouts/dashboard-layout'
import AuthWrapper from './components/auth-wrapper'
import LoginPage from './pages/login'
import SignupPage from './pages/signup'
import OnboardingPage from './pages/onboarding'
import DashboardPage from './pages/dashboard'
import TasksPage from './pages/tasks'
import ContributionsPage from './pages/contributions'
import CapTablePage from './pages/cap-table'
import SimulationsPage from './pages/simulations'
import UsersPage from './pages/users'
import InvitePage from './pages/invite'
import ProfilePage from './pages/profile'
import FundingRoundsPage from './pages/funding-rounds'
import EquityOffersPage from './pages/equity-offers'
import ValuationsPage from './pages/valuations'
import ExitScenariosPage from './pages/exit-scenarios'
import OptionTrackingPage from './pages/option-tracking'
import EquityRecommendationsPage from './pages/equity-recommendations'
import VestingDashboard from './pages/vesting-dashboard'
import ShareAuthorizationsPage from './pages/share-authorizations'
import OptionExercisesPage from './pages/option-exercises'
import MilestonesPage from './pages/milestones'
import ApiSettingsPage from './pages/api-settings'

// Lazy load public pages for better performance
const PublicLayout = lazy(() => import('./components/public/layout/PublicLayout'))
const HomePage = lazy(() => import('./components/public/pages/Home'))
const FeaturesPage = lazy(() => import('./components/public/pages/Features'))
const PricingPage = lazy(() => import('./components/public/pages/Pricing'))
const HowItWorksPage = lazy(() => import('./components/public/pages/HowItWorks'))
const AboutPage = lazy(() => import('./components/public/pages/About'))
const ContactPage = lazy(() => import('./components/public/pages/Contact'))

function App() {
  const { isLoaded, isSignedIn } = useAuthContext()

  if (!isLoaded) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    )
  }

  const LoadingFallback = () => (
    <div className="flex items-center justify-center min-h-screen">
      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
    </div>
  )

  return (
    <>
      <Toaster position="top-right" />
      <Suspense fallback={<LoadingFallback />}>
        <Routes>
          {/* Public marketing routes - accessible to everyone */}
          <Route element={<PublicLayout />}>
            <Route path="/" element={<HomePage />} />
            <Route path="/features" element={<FeaturesPage />} />
            <Route path="/pricing" element={<PricingPage />} />
            <Route path="/how-it-works" element={<HowItWorksPage />} />
            <Route path="/about" element={<AboutPage />} />
            <Route path="/contact" element={<ContactPage />} />
          </Route>

          {/* Auth routes - with catch-all for Clerk's built components */}
          <Route element={<AuthLayout />}>
            <Route path="/login/*" element={!isSignedIn ? <LoginPage /> : <Navigate to="/dashboard" />} />
            <Route path="/signup/*" element={!isSignedIn ? <SignupPage /> : <Navigate to="/dashboard" />} />
            <Route path="/invite/:token" element={<InvitePage />} />
          </Route>
          
          {/* Onboarding route - protected but accessible during signup flow */}
          <Route path="/onboarding" element={isSignedIn ? <OnboardingPage /> : <Navigate to="/login" />} />

          {/* Protected routes - all require authentication */}
          <Route element={isSignedIn ? <AuthWrapper><DashboardLayout /></AuthWrapper> : <Navigate to="/login" />}>
            <Route path="/dashboard" element={<DashboardPage />} />
            <Route path="/tasks" element={<TasksPage />} />
            <Route path="/contributions" element={<ContributionsPage />} />
            <Route path="/cap-table" element={<CapTablePage />} />
            <Route path="/simulations" element={<SimulationsPage />} />
            <Route path="/users" element={<UsersPage />} />
            <Route path="/profile" element={<ProfilePage />} />
            <Route path="/funding-rounds" element={<FundingRoundsPage />} />
            <Route path="/equity-offers" element={<EquityOffersPage />} />
            <Route path="/valuations" element={<ValuationsPage />} />
            <Route path="/exit-scenarios" element={<ExitScenariosPage />} />
            <Route path="/option-tracking" element={<OptionTrackingPage />} />
            <Route path="/equity-recommendations" element={<EquityRecommendationsPage />} />
            <Route path="/vesting" element={<VestingDashboard />} />
            <Route path="/share-authorizations" element={<ShareAuthorizationsPage />} />
            <Route path="/option-exercises" element={<OptionExercisesPage />} />
            <Route path="/milestones" element={<MilestonesPage />} />
            <Route path="/api-settings" element={<ApiSettingsPage />} />
          </Route>

          {/* Catch-all route - redirect to home for unmatched routes */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </Suspense>
    </>
  )
}

export default App